import React, { useState, useCallback } from 'react';
import T from 'prop-types';

import { isEmpty } from 'lodash-es';
import UserAuthForm from '../components/UserAuthForm';
import ConfirmationStep from '../components/ConfirmationStep';
import SuccessStep from '../components/SuccessStep';
import ForgotPassword from '../components/ForgotPassword';
import SuccessForgotStep from '../components/SuccessForgotStep';

import PressContainer from '../../../press-register/containers/PageContainer';

import { login, forgotPassword, resendConfirmation, sendPressMagicLink } from '../api';

const PageContainer = ({ links, formError }) => {
  const [step, setStep] = useState('userForm');
  const [processing, setProcessing] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [pressAccessEndedOn, setPressAccessEndedOn] = useState(null);
  const [responseError, setResponseError] = useState({});

  const setConfirmStep = useCallback((email) => {
    setUserEmail(email);
    setStep('confirmStep');
  }, []);

  const setSuccessStep = useCallback(() => {
    setStep('successStep');
  }, []);

  const setForgotPassword = useCallback((email) => {
    setResponseError({});
    setUserEmail(email);
    setStep('forgotPassword');
  }, []);

  const handleError = (err) => {
    const errors = err.response && err.response.data && err.response.data.errors;
    const emailError = errors && errors.email;
    const errorMessage = errors && errors.message;
    if (emailError) {
      setResponseError({ email: `Email ${emailError[0]}` });
    } else if (errorMessage) {
      setResponseError({ email: errorMessage });
    } else {
      setResponseError({ email: 'API error' });
    }
  };

  const handleUserFormSubmit = (userData) => {
    login({ user: userData })
      .then(({ data }) => {
        window.location.href = data.location;
      })
      .catch((error) => {
        setResponseError(error.response.data.errors);
      });
  };

  const handleResendClick = () => {
    resendConfirmation({ user: { email: userEmail } })
      .then(() => {
        setSuccessStep();
      })
      .catch((error) => {
        setResponseError(error.response.data.errors);
      });
  };

  const handleLoginClick = () => setStep('userForm');

  const handleForgotPasswordClick = (email) => {
    setResponseError({});
    setUserEmail(email);
    forgotPassword({ user: { email } })
      .then(() => {
        setStep('successForgotStep');
      })
      .catch((error) => handleError(error));
  };

  const handlePressMagicLink = (email, raiseError = false) => {
    setResponseError({});
    setUserEmail(email);

    return sendPressMagicLink({ user: { email } })
      .then(() => {
        setStep('pressInbox');
      })
      .catch((error) => {
        handleError(error);
        if (raiseError) throw error;
      });
  };

  const handlePressResendClick = () => {
    setProcessing(true);
    return handlePressMagicLink(userEmail).finally(() => setProcessing(false));
  };

  const handlePressExpired = (email, accessEndedOn) => {
    setResponseError({});
    setUserEmail(email);
    setPressAccessEndedOn(accessEndedOn || null);
    setStep('pressExpired');
  };

  if (step === 'pressInbox') {
    return (
      <PressContainer
        initialView="check_inbox"
        initialEmail={userEmail}
        error={responseError.email}
        onResendClick={handlePressResendClick}
      />
    );
  }

  if (step === 'pressExpired') {
    return (
      <PressContainer
        initialView="expired"
        initialEmail={userEmail}
        expiredOn={pressAccessEndedOn}
      />
    );
  }

  return (
    <div className="user-auth-page">
      <header className="user-auth-page__header">
        <a className="logo flex-no-shrink" data-label="Apptopia Logo" href="https://apptopia.com/">
          <span className="hidden">Apptopia Logo</span>
        </a>
      </header>
      <div className="user-auth-container">
        {!isEmpty(formError) && <div className="user-auth-form-error">{formError}</div>}
        {step === 'userForm' && (
          <UserAuthForm
            email={userEmail}
            links={links}
            processing={processing}
            responseError={responseError}
            setProcessing={setProcessing}
            onConfirmationRequred={setConfirmStep}
            onForgotClick={setForgotPassword}
            onPressExpired={handlePressExpired}
            onPressMagicLink={handlePressMagicLink}
            onUserFormSubmit={handleUserFormSubmit}
          />
        )}
        {step === 'forgotPassword' && (
          <ForgotPassword
            email={userEmail}
            responseError={responseError}
            onForgotFormSubmit={handleForgotPasswordClick}
            onLoginClick={handleLoginClick}
          />
        )}
        {step === 'confirmStep' && <ConfirmationStep onResendClick={handleResendClick} />}
        {step === 'successStep' && <SuccessStep onLoginClick={handleLoginClick} />}
        {step === 'successForgotStep' && (
          <SuccessForgotStep email={userEmail} onLoginClick={handleLoginClick} />
        )}
      </div>
      <footer className="user-auth-page__footer">
        Copyright © {new Date().getFullYear()} Apptopia Inc. All rights reserved.
      </footer>
    </div>
  );
};

PageContainer.propTypes = {
  links: T.object,
  formError: T.array,
};

export default PageContainer;
